/**
 * Meter dial — needle + simple center cap. The cap is a two-tone
 * bullseye (outer disc in dial color, inner pinhole in background
 * color). The needle keeps its drop shadow + round cap for legibility.
 */

import type { CanvasColor } from "../core";
import { Canvas } from "../core";
import type { MeterConfig } from "./config";
import { getComputedVariable } from "@utils/utils";

export interface DialState {
    color: CanvasColor;
    length: number;
    width: number;
    dotRadius: number;
    base: number;
}

export function createDial(config: MeterConfig, dialColor: string): DialState {
    return {
        color: dialColor,
        length: config.dialLength,
        width: config.dialWidth,
        dotRadius: config.dotRadius,
        base: config.base,
    };
}

/**
 * Draw the dial needle and the simple center cap.
 */
export function drawDial(
    canvas: Canvas,
    dial: DialState,
    theta: number,
): void {
    const ctx = canvas.ctx;
    const ox = canvas.originX;
    const oy = canvas.originY;

    // ── Dial needle with subtle drop shadow ──
    ctx.save();
    ctx.strokeStyle = dial.color as string;
    ctx.lineWidth = dial.width;
    ctx.lineCap = "round";
    ctx.shadowColor = "rgba(0,0,0,0.18)";
    ctx.shadowBlur = dial.base * 0.4;
    ctx.beginPath();
    ctx.moveTo(ox, oy);
    ctx.lineTo(
        ox + Math.cos(theta) * dial.length,
        oy + Math.sin(theta) * dial.length,
    );
    ctx.stroke();
    ctx.restore();

    drawCenter(ctx, ox, oy, dial.dotRadius, dial.color as string, dial.base);
}

/**
 * Simple two-tone bullseye center cap.
 *
 *   - Outer disc in the dial color (no gradient, no facets, no specular)
 *   - Inner pinhole in the background color for the bullseye cutout
 *
 * `base` retained in the signature for source-compat with paint callers
 * that pass it; it is not consumed by the simple cap.
 */
function drawCenter(
    ctx: CanvasRenderingContext2D,
    ox: number,
    oy: number,
    dotRadius: number,
    dialColor: string,
    _base: number,
): void {
    const bgColor = getComputedVariable("--background") || "rgba(255,255,255,0.9)";

    ctx.save();
    ctx.fillStyle = dialColor;
    ctx.beginPath();
    ctx.arc(ox, oy, dotRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.save();
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.arc(ox, oy, dotRadius * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}
